// Tenant security policy — admin-only API (ANX-013 + ANX-046).
import type { ApiClient } from './client';

export type BanPolicy = 'admin_only' | 'any_operator';
// ANX-070: стратегия авто-назначения диалогов.
export type RoutingStrategy = 'off' | 'round_robin' | 'least_busy';

export interface TenantSecurityPolicy {
  requireMfa: boolean;
  banPolicy: BanPolicy;
  // ANX-071: лимит одновременно открытых диалогов на оператора (0 = без лимита).
  maxConcurrentConversations: number;
  // ANX-070: стратегия авто-назначения новых диалогов.
  routingStrategy: RoutingStrategy;
  // ANX-073: SLA первого ответа (минуты; 0 = выкл) + учёт рабочих часов.
  slaResponseMinutes: number;
  slaRespectOfficeHours: boolean;
  // ANX-079: задержка перед follow-up email (минуты).
  followUpDelayMinutes: number;
  updatedAt?: string;
  updatedBy?: string;
}

interface RawPolicy {
  require_mfa: boolean;
  ban_policy: BanPolicy;
  max_concurrent_conversations?: number;
  routing_strategy?: RoutingStrategy;
  sla_response_minutes?: number;
  sla_respect_office_hours?: boolean;
  follow_up_delay_minutes?: number;
  updated_at?: string;
  updated_by?: string;
}

function toPolicy(raw: RawPolicy): TenantSecurityPolicy {
  const p: TenantSecurityPolicy = {
    requireMfa: raw.require_mfa,
    banPolicy: raw.ban_policy ?? 'admin_only',
    maxConcurrentConversations: raw.max_concurrent_conversations ?? 0,
    routingStrategy: raw.routing_strategy ?? 'off',
    slaResponseMinutes: raw.sla_response_minutes ?? 0,
    slaRespectOfficeHours: raw.sla_respect_office_hours !== false,
    followUpDelayMinutes: raw.follow_up_delay_minutes ?? 30,
  };
  if (raw.updated_at) p.updatedAt = raw.updated_at;
  if (raw.updated_by) p.updatedBy = raw.updated_by;
  return p;
}

export async function getTenantSecurityPolicy(client: ApiClient): Promise<TenantSecurityPolicy> {
  const raw = await client.request<RawPolicy>('/api/v1/tenant/security-policy');
  return toPolicy(raw);
}

export async function patchTenantSecurityPolicy(
  client: ApiClient,
  patch: {
    requireMfa?: boolean;
    banPolicy?: BanPolicy;
    maxConcurrentConversations?: number;
    routingStrategy?: RoutingStrategy;
    slaResponseMinutes?: number;
    slaRespectOfficeHours?: boolean;
    followUpDelayMinutes?: number;
  },
): Promise<TenantSecurityPolicy> {
  const body: Record<string, unknown> = {};
  if (patch.requireMfa !== undefined) body.require_mfa = patch.requireMfa;
  if (patch.banPolicy !== undefined) body.ban_policy = patch.banPolicy;
  if (patch.maxConcurrentConversations !== undefined) {
    body.max_concurrent_conversations = patch.maxConcurrentConversations;
  }
  if (patch.routingStrategy !== undefined) body.routing_strategy = patch.routingStrategy;
  if (patch.slaResponseMinutes !== undefined) body.sla_response_minutes = patch.slaResponseMinutes;
  if (patch.slaRespectOfficeHours !== undefined) {
    body.sla_respect_office_hours = patch.slaRespectOfficeHours;
  }
  if (patch.followUpDelayMinutes !== undefined) {
    body.follow_up_delay_minutes = patch.followUpDelayMinutes;
  }
  const raw = await client.request<RawPolicy>('/api/v1/tenant/security-policy', {
    method: 'PATCH',
    json: body,
  });
  return toPolicy(raw);
}
